/**
 * Ingest-API (Spec 5) — das einheitliche Ticket-Schema fuer alle Quell-Apps.
 *
 * **Single Source of Truth fuer drei Seiten** (Invariante 2): die Fastify-Routen
 * validieren damit, `@tickethub/sdk` typisiert damit seine Methoden, und
 * `docs/openapi.json` wird daraus generiert. Kein Paket definiert eigene
 * Ingest-Schemas.
 *
 * Getrennt von `schemas/ticket.ts`, weil das eine andere Sicht ist: dort steht
 * das Ticket, wie das Admin-UI es kennt (Queue, Zuweisung, Tags, interne
 * Notizen), hier das, was eine Quell-App schickt und sehen darf. Der
 * Unterschied ist keine Bequemlichkeit, sondern eine Sicherheitsgrenze:
 * `publicTicketMessageSchema` kennt **kein** `internal_note`.
 */
import { z } from 'zod';
import { emailSchema, uuidSchema } from './common.js';
import { tiptapDocSchema } from './richtext.js';
import { metadataSchema } from '../metadata.js';
import { ticketNumberSchema, ticketPrioritySchema, ticketStatusSchema } from './ticket.js';
/** Der Endkunde in der Quell-App (Spec 5.2). */
export const ingestRequesterSchema = z.object({
    /** ID des Endkunden in der App — Grundlage des Identitaets-Matchings. */
    external_user_id: z.string().min(1).max(200).nullable().default(null),
    email: emailSchema.nullable().default(null),
    name: z.string().min(1).max(200).nullable().default(null),
});
/**
 * Nachrichtentext. Die Spec erlaubt `{text, html}` **oder** TipTap-JSON —
 * beides wird serverseitig zu `body_json` normalisiert, gerendert und
 * sanitisiert (`services/richtext.ts`). Client-HTML wird nie uebernommen.
 */
export const ingestMessageBodySchema = z
    .object({
    text: z.string().max(100_000).nullable().default(null),
    html: z.string().max(500_000).nullable().default(null),
    body_json: tiptapDocSchema.optional(),
})
    .refine((input) => input.body_json !== undefined ||
    (input.text !== null && input.text.trim().length > 0) ||
    (input.html !== null && input.html.trim().length > 0), { message: 'Die Nachricht braucht text, html oder body_json.' });
/** `POST /api/v1/tickets` — exakt Spec 5.2. */
export const createIngestTicketSchema = z.object({
    /** ID im Quellsystem. Pflicht und **idempotent** (Spec 5.2). */
    external_ref: z.string().min(1).max(200),
    subject: z.string().min(1).max(500),
    message: ingestMessageBodySchema,
    requester: ingestRequesterSchema,
    priority: ticketPrioritySchema.default('normal'),
    /** Frei je Quelle; validiert gegen `sources.metadata_schema` (ADR 0006). */
    metadata: metadataSchema.default({}),
    /** Vorher ueber `POST /api/v1/attachments` hochgeladen. */
    attachment_ids: z.array(uuidSchema).max(20).default([]),
    /** Wann das Problem in der App auftrat — nur informativ, landet in `metadata`. */
    occurred_at: z.iso.datetime().nullable().default(null),
});
/**
 * `POST /api/v1/tickets/:id/messages` — Kundenantwort aus der App.
 *
 * `requester` ist optional: fehlt er, gilt der Kontakt des Tickets. Wird einer
 * mitgeschickt, laeuft das Identitaets-Matching aus Spec 5.2 erneut — so
 * lernen Contacts dazu, wenn die App inzwischen eine E-Mail-Adresse kennt.
 */
export const createIngestMessageSchema = z.object({
    message: ingestMessageBodySchema,
    requester: ingestRequesterSchema.optional(),
    attachment_ids: z.array(uuidSchema).max(20).default([]),
});
/**
 * `PATCH /api/v1/tickets/:id` — Statuswunsch aus dem Quellsystem (Spec 5.1,
 * z. B. „Kunde schliesst das Ticket in der App").
 *
 * Nur diese drei Felder: Zuweisung, Queue und Tags gehoeren der Zentrale
 * (Invariante 4 — die Zentrale ist source of truth fuer die Bearbeitung).
 */
export const updateIngestTicketSchema = z.object({
    status: ticketStatusSchema.optional(),
    priority: ticketPrioritySchema.optional(),
    /** Wird mit den bestehenden Metadaten **verschmolzen**, nicht ersetzt. */
    metadata: metadataSchema.optional(),
});
/**
 * `POST /api/v1/tickets/:id/replies` — Bearbeiter-Antwort aus dem Quellsystem
 * (ADR 0018: Bearbeitung in beiden Systemen).
 *
 * Das Gegenstueck zu `createIngestMessageSchema`: dort schreibt der Endkunde
 * (`inbound`), hier der Bearbeiter der Quell-App (`outbound`). Bewusst ohne
 * `requester` — der Absender ist die App selbst, kein Kontakt. Die
 * Statusautomatik aus Spec 3.3 laeuft mit (`open`/`pending` →
 * `waiting_customer`); der Folgezustand steht in der Antwort.
 */
export const createIngestReplySchema = z.object({
    message: ingestMessageBodySchema,
    /** Name des Bearbeiters in der Quell-App — reine Anzeige, kein Konto. */
    author_name: z.string().min(1).max(200).nullable().default(null),
    attachment_ids: z.array(uuidSchema).max(20).default([]),
});
// ─────────────────────────────────────────────────────────── Ausgabe
/**
 * Anhang in der Ingest-Sicht. Bewusst **ohne** `download_url`: die signierte
 * URL aus Phase 1 setzt zusaetzlich eine Agent-Session voraus und waere fuer
 * eine Quell-App wertlos.
 */
export const ingestAttachmentSchema = z.object({
    id: uuidSchema,
    filename: z.string(),
    mime: z.string(),
    size: z.number().int().min(0),
});
/**
 * Nachricht in der Ingest-Sicht.
 *
 * **`internal_note` fehlt hier absichtlich.** Interne Notizen verlassen die
 * Zentrale nie (Spec 3.2) — die Einschraenkung steht im Typ, damit sie nicht
 * von einer vergessenen Filterbedingung abhaengt.
 */
export const publicMessageKindSchema = z.enum(['inbound', 'outbound']);
export const publicTicketMessageSchema = z.object({
    id: uuidSchema,
    kind: publicMessageKindSchema,
    /** `api` | `email` | `ui` — woher die Nachricht kam. */
    via: z.enum(['api', 'email', 'ui']),
    /** Bearbeiter-Name aus der Quell-App (ADR 0018) — sonst `null`. */
    author_name: z.string().nullable(),
    body_text: z.string(),
    body_html: z.string(),
    attachments: z.array(ingestAttachmentSchema),
    created_at: z.iso.datetime(),
});
/** Ticket in der Ingest-Sicht — das, was `GET /api/v1/tickets/:id` liefert. */
export const publicTicketSchema = z.object({
    id: uuidSchema,
    number: ticketNumberSchema,
    external_ref: z.string().nullable(),
    subject: z.string(),
    status: ticketStatusSchema,
    priority: ticketPrioritySchema,
    metadata: metadataSchema,
    requester: z.object({
        contact_id: uuidSchema,
        name: z.string(),
        email: z.string().nullable(),
    }),
    messages: z.array(publicTicketMessageSchema),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
});
/**
 * Antwort von `POST /api/v1/tickets/:id/replies` (ADR 0018): das Ticket
 * **plus** die ID der eben angelegten Nachricht — explizit statt „die letzte
 * im Array", damit die Quell-App einen eindeutigen Dedup-Anker hat.
 */
export const ingestReplyResultSchema = z.object({
    ticket: publicTicketSchema,
    message_id: uuidSchema,
});
//# sourceMappingURL=ingest.js.map